/**
 * WHEN THE OVERVIEW SHOWS THE NETWORK (2026-10-26), TWO THINGS OUTSIDE IT MUST KNOW.
 *
 *   The header scope: while the network is on screen it names no site — "Thunder Energy / Network" —
 *   since no site is what the page is showing (`HeaderScope.tsx`). Set by the Overview as it shows
 *   the network, cleared as it stops or unmounts, so no other screen ever reads it as true.
 *
 *   The person's last choice of the two (Hamzah, 2026-10-26: "remember the last choice per browser, the
 *   way theme is remembered"): the Overview opens on the network only while nothing has been chosen.
 *   Per browser, in `localStorage`, read and written defensively — a private window or blocked storage
 *   simply forgets, and the page still opens.
 */
import { useSyncExternalStore } from 'react';

let shown = false;
const listeners = new Set<() => void>();

export function setNetworkShown(value: boolean): void {
  if (shown === value) return;
  shown = value;
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useNetworkShown(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => shown,
    () => false,
  );
}

const KEY = 'lumen.overview.view';

export type OverviewChoice = 'site' | 'network';

export function readOverviewChoice(): OverviewChoice | null {
  try {
    const v = window.localStorage.getItem(KEY);
    return v === 'site' || v === 'network' ? v : null;
  } catch {
    return null;
  }
}

export function writeOverviewChoice(choice: OverviewChoice): void {
  try {
    window.localStorage.setItem(KEY, choice);
  } catch {
    /* Storage refused: the choice lasts this visit only. */
  }
}
